import React, { useMemo } from "react";
import { cn } from "@utils";

function CircularCountdown({
  totalMs,
  remainingMs,
  isPaused = false,
  size = 30,
  strokeWidth = 3,
  progressColorClass = "text-violet-500",
  trackColorClass = "text-transparent",
  className = "bg-black rounded-full",
  showSecondsSuffix = true,
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);
  const innerIconSize = useMemo(
    () => Math.max(8, Math.floor(size * 0.45)),
    [size]
  );

  const safeTotal = Math.max(1, Number(totalMs) || 1);
  const safeRemaining = Math.max(0, Number(remainingMs) || 0);

  const progress = Math.min(1, Math.max(0, 1 - safeRemaining / safeTotal));
  const dashOffset = circumference * (1 - progress);
  const secondsLeft = Math.max(0, Math.ceil(safeRemaining / 1000));

  return (
    <div
      className={cn(
        "relative grid place-items-center",
        className,
        "bg-black rounded-full"
      )}
      style={{ width: size, height: size }}
      role="timer"
      aria-label={
        isPaused
          ? "Countdown paused"
          : `Countdown: ${secondsLeft} seconds remaining`
      }
    >
      <svg width={size} height={size} className="rotate-[-90deg]" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={cn(
            progressColorClass,
            "transition-[stroke-dashoffset] duration-100 ease-linear will-change-[stroke-dashoffset]"
          )}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transitionProperty: "stroke-dashoffset" }}
        />
      </svg>

      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <svg
          width={innerIconSize}
          height={innerIconSize}
          viewBox="0 0 24 24"
          aria-hidden
          className={cn(
            "text-white transition-opacity duration-200 ease-in-out",
            isPaused ? "opacity-60" : "opacity-0"
          )}
        >
          <rect
            x="6"
            y="4"
            width="4"
            height="16"
            rx="1.5"
            fill="currentColor"
          />
          <rect
            x="14"
            y="4"
            width="4"
            height="16"
            rx="1.5"
            fill="currentColor"
          />
        </svg>
      </div>

      <div className="absolute inset-0 grid place-items-center select-none font-bold text-white text-xs">
        <span
          className={cn(
            "tabular-nums text-white transition-opacity duration-200 ease-in-out",
            isPaused ? "opacity-0" : "opacity-80"
          )}
        >
          {secondsLeft}
          {showSecondsSuffix ? "s" : ""}
        </span>
      </div>
    </div>
  );
}

export default CircularCountdown;
